import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { CsvAgentResultFields } from '@/composables/useDromoAgentCsvUpload';
import type { EventAgentsBulkCreateCompleteFailure } from '@/types/websocket';

/**
 * A single provider–athlete pair as submitted to the bulk endpoint, kept so an
 * upload failure can be matched back to the row that produced it.
 */
export type AgentBulkUploadRow = CsvAgentResultFields & {
  athleteTwrn: string;
};

export type AgentBulkUploadFailedRow = {
  row: AgentBulkUploadRow;
  code: string;
};

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export const useAgentBulkUploadStore = defineStore('agentBulkUpload', () => {
  const submittedRows = ref<AgentBulkUploadRow[]>([]);
  const failedRows = ref<AgentBulkUploadFailedRow[]>([]);
  const reopenRequested = ref(false);

  const hasFailures = computed(() => failedRows.value.length > 0);

  /** Records the rows of the most recent submission and drops any failures from a previous one. */
  const setSubmittedRows = (rows: AgentBulkUploadRow[]): void => {
    submittedRows.value = rows;
    failedRows.value = [];
  };

  /**
   * Finds the submitted row a single failure refers to, by the lowercased agent email plus the
   * athlete twrn. The failure's `email` is the agent's email — both agents and athletes have
   * one, so it is always compared against the row's `agentEmail`, never the athlete's. When the
   * event omits `athlete_twrn` the agent email alone is used, but only if it is unambiguous.
   */
  const findSubmittedRow = (failure: EventAgentsBulkCreateCompleteFailure): AgentBulkUploadRow | undefined => {
    const failureAgentEmail = normalizeEmail(failure.email);
    const rowsWithAgentEmail = submittedRows.value.filter(
      (submittedRow) => normalizeEmail(submittedRow.agentEmail) === failureAgentEmail,
    );

    if (failure.athlete_twrn !== null) {
      return rowsWithAgentEmail.find((submittedRow) => submittedRow.athleteTwrn === failure.athlete_twrn);
    }

    return rowsWithAgentEmail.length === 1 ? rowsWithAgentEmail[0] : undefined;
  };

  /** Matches the complete event's `failed[]` back to the submitted rows. Unmatchable entries are ignored. */
  const applyFailures = (failures: EventAgentsBulkCreateCompleteFailure[]): void => {
    const matchedFailures: AgentBulkUploadFailedRow[] = [];
    const alreadyMatched = new Set<AgentBulkUploadRow>();

    for (const failure of failures) {
      const submittedRow = findSubmittedRow(failure);
      if (!submittedRow || alreadyMatched.has(submittedRow)) continue;

      alreadyMatched.add(submittedRow);
      matchedFailures.push({
        row: submittedRow,
        code: failure.code,
      });
    }

    failedRows.value = matchedFailures;
  };

  const requestReopen = (): void => {
    reopenRequested.value = true;
  };

  const clearReopen = (): void => {
    reopenRequested.value = false;
  };

  return {
    submittedRows,
    failedRows,
    reopenRequested,
    hasFailures,
    setSubmittedRows,
    applyFailures,
    requestReopen,
    clearReopen,
  };
});
